import { ReadingStatus } from "../types";
import type { JournalFilters } from "../services/journalService";

export interface BookListFilters {
  q?: string;
  status?: ReadingStatus | "all";
  sort?: "title" | "author" | "created_at" | "updated_at" | "last_read_at";
  direction?: "asc" | "desc";
  page?: number;
  page_size?: number;
  collection_id?: number;
  tag_ids?: number[];
  is_favorite?: boolean;
  location_id?: number;
}
export interface SessionListParams {
  book_id?: number;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

export function canonicalBookFilters(filters: BookListFilters = {}): BookListFilters {
  const q = filters.q?.trim();
  return {
    ...(q ? { q } : {}),
    ...(filters.status && filters.status !== "all" ? { status: filters.status } : {}),
    ...(filters.sort && filters.sort !== "created_at" ? { sort: filters.sort } : {}),
    ...(filters.direction && filters.direction !== "desc"
      ? { direction: filters.direction }
      : {}),
    ...(filters.page && filters.page !== 1 ? { page: filters.page } : {}),
    ...(filters.page_size && filters.page_size !== 20
      ? { page_size: filters.page_size }
      : {}),
    ...(filters.collection_id ? { collection_id: filters.collection_id } : {}),
    ...(filters.tag_ids?.length ? { tag_ids: [...filters.tag_ids].sort() } : {}),
    ...(filters.is_favorite ? { is_favorite: true } : {}),
    ...(filters.location_id ? { location_id: filters.location_id } : {}),
  };
}

export const queryKeys = {
  auth: { me: () => ["auth", "me"] as const },
  books: {
    all: () => ["books"] as const,
    lists: () => ["books", "list"] as const,
    list: (filters: BookListFilters = {}) =>
      ["books", "list", canonicalBookFilters(filters)] as const,
    archived: () => ["books", "archived"] as const,
    detail: (bookId: number) => ["books", "detail", bookId] as const,
    series: (bookId: number) => ["books", "detail", bookId, "series"] as const,
    editionProgress: (bookId: number) =>
      ["books", "detail", bookId, "edition-progress"] as const,
    readingSummary: (bookId: number) =>
      ["books", "detail", bookId, "reading-summary"] as const,
    undoHistory: () => ["books", "undo-history"] as const,
  },
  sessions: {
    all: () => ["sessions"] as const,
    list: (params: SessionListParams = {}) => ["sessions", "list", params] as const,
    today: () => ["sessions", "today"] as const,
    book: (bookId: number) => ["sessions", "book", bookId] as const,
    forecast: (bookId: number) => ["sessions", "forecast", bookId] as const,
  },
  readingCycles: {
    all: () => ["reading-cycles"] as const,
    entry: (bookId: number) => ["reading-cycles", bookId] as const,
  },
  journal: {
    all: () => ["journal"] as const,
    list: (filters: JournalFilters = {}) => ["journal", "list", filters] as const,
    book: (bookId: number) =>
      ["journal", "list", { library_entry_id: bookId }] as const,
    reflectionPrompts: () => ["journal", "reflection-prompts"] as const,
    reflectionHistory: (entryId: number) =>
      ["journal", "reflection-history", entryId] as const,
  },
  stats: {
    all: () => ["stats"] as const,
    user: () => ["stats", "user"] as const,
    reading: () => ["stats", "reading"] as const,
    habits: (days: number) => ["stats", "habits", days] as const,
    monthly: (year?: number) => ["stats", "monthly", year ?? "current"] as const,
    bookProgress: (bookId?: number) =>
      ["stats", "book-progress", bookId ?? "all"] as const,
    weeklyGoals: () => ["stats", "weekly-goals"] as const,
  },
  goals: {
    all: () => ["reading-goals"] as const,
    list: () => ["reading-goals", "list"] as const,
  },
  organization: {
    all: () => ["library-organization"] as const,
    collections: () => ["library-organization", "collections"] as const,
    tags: () => ["library-organization", "tags"] as const,
  },
  locations: {
    all: () => ["library-locations"] as const,
    list: () => ["library-locations", "list"] as const,
  },
  libraryViews: {
    all: () => ["library-views"] as const,
    list: () => ["library-views", "list"] as const,
  },
  recommendations: {
    all: () => ["recommendations"] as const,
    list: (page: number) => ["recommendations", "list", page] as const,
    preferences: () => ["recommendations", "preferences"] as const,
  },
} as const;
